import React from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { InvestmentRoundingSetting } from 'shared';
import { twMerge } from 'tailwind-merge';

import {
  InvestmentRoundingSettingTitle,
  InvestmentRoundingSettingTooltipDesc,
  investmentRoundingTooltipSectionOrder,
} from '../types/investment-rounding.types';
import { AppFormattedMessage } from './app-formatted-message/index';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './hover-card/index';
import { FormRadio, FormRadioGroup } from './radio-group/index';

export type RoundingSettingFieldProps<T extends FieldValues> = {
  control: Control<T>;
  disabled?: boolean;
  name: FieldPath<T>;
};

export const RoundingSettingField = <T extends FieldValues>({
  control,
  name,
  disabled,
}: RoundingSettingFieldProps<T>) => (
  <div className="flex flex-col gap-2">
    <div className="flex w-full items-center justify-between">
      <span className="text-sm font-[550] text-gray-700">
        <AppFormattedMessage id="ROUNDING_SETTINGS" />
      </span>
      <HoverCard openDelay={100}>
        <HoverCardTrigger asChild>
          <button className="cursor-pointer text-label-sm font-medium text-brand-700" type="button">
            <AppFormattedMessage id="HOW_ROUNDING_WORKS" />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="end"
          className="w-fit rounded-lg bg-gray-900 px-3 py-4 shadow-lg"
          isShownTriangle={false}
          side="top"
          sideOffset={10}
        >
          <div className="flex flex-col divide-y divide-gray-800">
            {investmentRoundingTooltipSectionOrder.map((setting) => (
              <div
                className="flex flex-col gap-1 py-3 text-white first:pt-0 last:pb-0"
                key={setting}
              >
                <span className="text-sm font-[550]">
                  <AppFormattedMessage id={InvestmentRoundingSettingTitle[setting]} />
                </span>
                <span className="text-nowrap text-xs">
                  <AppFormattedMessage id={InvestmentRoundingSettingTooltipDesc[setting]} />
                </span>
              </div>
            ))}
          </div>
          <span
            aria-hidden
            className="absolute -bottom-1.5 right-4 size-3 rotate-45 bg-[#101828]"
          />
        </HoverCardContent>
      </HoverCard>
    </div>
    <FormRadioGroup
      className="h-fit divide-x divide-[#EAECF0] overflow-hidden rounded-[4px]"
      control={control}
      disabled={disabled}
      name={name}
      wrapperClassName={twMerge(disabled && 'pointer-events-none')}
    >
      {Object.values(InvestmentRoundingSetting).map((setting) => (
        <FormRadio
          className={twMerge(
            'h-11 text-nowrap bg-gray-50 px-4 py-3 !text-label-md font-medium text-gray-700 data-[checked]:bg-brand-700 data-[checked]:text-white',
            disabled &&
              'cursor-default text-gray-300 data-[checked]:bg-gray-50 data-[checked]:text-gray-300',
          )}
          control={control}
          disabled={disabled}
          key={setting}
          name={name}
          styleType="NONE"
          value={setting}
        >
          <AppFormattedMessage id={InvestmentRoundingSettingTitle[setting]} />
        </FormRadio>
      ))}
    </FormRadioGroup>
  </div>
);
